import { useEffect, useMemo } from "react";

import { useLocation } from "react-router-dom";
import { Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import {
  useGetAreasOfExpertiseQuery,
  useGetCountriesQuery,
} from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { NormalizedApiError } from "@/redux/types";
import {
  RegisterFormValues,
  SelectOption,
  UseRegisterFormConfig,
} from "../types";

export const useRegisterForm = <T extends RegisterFormValues>({
  useVerifyToken,
  schema,
  defaultValues,
  buildResetValues,
}: UseRegisterFormConfig<T>) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") ?? "";

  const { data, isSuccess, error, isFetching } = useVerifyToken(token, {
    skip: !token,
  });

  const { data: countries } = useGetCountriesQuery({ pagination: "off" });
  const { data: areasOfExpertise } = useGetAreasOfExpertiseQuery({
    ids: [],
    pagination: "off",
  });

  const countryOptions = useMemo((): SelectOption[] => {
    if (!countries) return [];
    const countryData = isPaginatedResponse(countries)
      ? countries.results
      : countries;
    return countryData.map((i) => ({ value: i.id, label: i.name }));
  }, [countries]);

  const areaOfExpertiseOptions = useMemo((): SelectOption[] => {
    if (!areasOfExpertise) return [];
    const expertiseData = isPaginatedResponse(areasOfExpertise)
      ? areasOfExpertise.results
      : areasOfExpertise;
    return expertiseData.map((i) => ({ value: i.id, label: i.name }));
  }, [areasOfExpertise]);

  const form = useForm<T>({
    resolver: yupResolver(schema(t)) as Resolver<T>,
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    if (isSuccess && data && !form.formState.isDirty) {
      form.reset(buildResetValues(data, token));
    }
  }, [isSuccess, data, form, token, buildResetValues]);

  return {
    form,
    data,
    isSuccess,
    error: error as NormalizedApiError | undefined,
    isFetching,
    countryOptions,
    areaOfExpertiseOptions,
  };
};
